import { api } from "@/lib/api";

/**
 * Thin axios wrappers around /api/web/admin/* endpoints.
 *
 * Why a separate module? Three reasons:
 *   - Keeps the /admin surface visually distinct in IDE search.
 *   - Lets the AdminGuard call .me() without pulling in the entire api.js graph.
 *   - All the admin endpoints share the same JWT auth as `api`, so they reuse
 *     the existing Authorization header + refresh interceptor.
 *
 * Every method returns response.data directly; throw on non-2xx.
 */

const BASE = "/api/web/admin";

function unwrap(res) {
  return res.data;
}

export const adminApi = {
  me: () => api.get(`${BASE}/me`).then(unwrap),
  stats: () => api.get(`${BASE}/stats`).then(unwrap),
  health: () => api.get(`${BASE}/health`).then(unwrap),

  // Lists
  stores: (params = {}) => api.get(`${BASE}/stores`, { params }).then(unwrap),
  competitors: (params = {}) =>
    api.get(`${BASE}/competitors`, { params }).then(unwrap),
  ads: (params = {}) => api.get(`${BASE}/ads`, { params }).then(unwrap),
  jobs: (params = {}) => api.get(`${BASE}/jobs`, { params }).then(unwrap),
  emails: (params = {}) => api.get(`${BASE}/emails`, { params }).then(unwrap),
  audit: (params = {}) => api.get(`${BASE}/audit`, { params }).then(unwrap),

  // Operations (mutating)
  ops: {
    syncAllStores: (storeIds = null) =>
      api
        .post(`${BASE}/ops/sync-all-stores`, storeIds ? { storeIds } : {})
        .then(unwrap),
    syncStore: (storeId) =>
      api.post(`${BASE}/ops/sync-store`, { storeId }).then(unwrap),
    syncCompetitor: (competitorId) =>
      api.post(`${BASE}/ops/sync-competitor`, { competitorId }).then(unwrap),
    dailyFullSync: () => api.post(`${BASE}/ops/daily-full-sync`).then(unwrap),
    classifyAds: ({ competitorId = null, storeId = null } = {}) =>
      api
        .post(`${BASE}/ops/classify-ads`, {
          ...(competitorId ? { competitorId } : {}),
          ...(storeId ? { storeId } : {}),
        })
        .then(unwrap),
    backfillLayerD: (competitorId = null) =>
      api
        .post(
          `${BASE}/ops/backfill-layerd`,
          competitorId ? { competitorId } : {},
        )
        .then(unwrap),
    bulkRescoreLayerD: (competitorId = null) =>
      api
        .post(
          `${BASE}/ops/bulk-rescore-layerd`,
          competitorId ? { competitorId } : {},
        )
        .then(unwrap),
    scraperProbe: () => api.get(`${BASE}/ops/scraper-probe`).then(unwrap),
    backfillMetaImages: (params = {}) =>
      api.post(`${BASE}/ops/backfill-meta-images`, params).then(unwrap),
  },

  waitlistEmail: {
    get: () => api.get(`${BASE}/waitlist-email`).then(unwrap),
    save: (body) => api.put(`${BASE}/waitlist-email`, body).then(unwrap),
    test: (body) => api.post(`${BASE}/waitlist-email/test`, body).then(unwrap),
  },
};
